import pluralize from "pluralize";

import dayjs from "../shared/helpers/dayjs";
import emarsys from "../shared/helpers/emarsys";
import { dynamicRemarketing, trackEnquiry, trackServerSideEnquiryError } from "../shared/helpers/gtm_tracking";

import initForm, { queryString } from "./Forms";

import { SUMMARY_DATE_FORMAT_SHORT, UK_DATE_FORMAT } from "../const/DateFormatConsts";

import { EMAIL_PATTERN, PHONE_PATTERN } from "../const/Patterns";

import initDropdownEvents from "../shared/helpers/dropdown_phone_code";

const submitBooking = (enquiry, data) => {
  const form = enquiry.querySelector(".js-booking-form");

  Object.keys(data).forEach(key => {
    form.querySelector(`input[name=${key}]`).value = data[key];
  });

  form.submit();
};

const trackRemarketing = (slug, price) => {
  dynamicRemarketing({
    hrental_id: slug,
    hrental_totalvalue: parseInt(price, 10),
    hrental_pagetype: "conversion"
  });
};

const toggleSubmitButtons = (enquiry, active) => {
  const enquireButton = enquiry.querySelector(".js-enquiry-submit");
  const bookButton = enquiry.querySelector(".js-booking-submit");

  if (active) {
    enquireButton.removeAttribute("disabled");

    if (bookButton) { bookButton.classList.remove("hide"); }
  } else {
    enquireButton.setAttribute("disabled", "");

    if (bookButton) { bookButton.classList.add("hide"); }
  }
};

const toggleErrorMessages = (enquiry, active) => {
  if (active) {
    enquiry.classList.add("has-failed");
  } else {
    enquiry.classList.remove("has-failed");
  }
};

const handleError = (form, className, msgError, element = "div") => {
  let fieldSelector = form.querySelector(className);

  if (fieldSelector) {
    fieldSelector.classList.add("is-invalid");
    fieldSelector = fieldSelector.closest(element);
    fieldSelector.querySelector(".fa-exclamation-triangle").classList.remove("hide");
    const htmlErrors = document.createElement("div");
    htmlErrors.classList.add("error-message", "js-error-message");
    htmlErrors.innerText = msgError;
    fieldSelector.after(htmlErrors);
  }
};

const scrollToElement = (form, className) => {
  const scrollToEl = form.querySelector(className);
  if (scrollToEl) {
    const navOffset = 135;
    const topOfElement = (window.pageYOffset + scrollToEl.getBoundingClientRect().top) - navOffset;
    window.scroll({ top: topOfElement, behavior: "smooth" });
  }
};

const getErrorMessages = (enquiry, res, form) => {
  let errorsHandled = 0;
  const resErrors = JSON.parse(res.responseText).errors;

  if (resErrors) {
    const keys = Object.keys(resErrors);
    const listFieldNameAndParent = {
      email: ["email address"],
      phone: ["phone number", ".layout__item__field"],
      first_name: ["first name"],
      last_name: ["last name"]
    };

    keys.forEach(key => {
      const fieldInfo = listFieldNameAndParent[key];

      if (fieldInfo) {
        const convertedKey = `.js-${key.replace(/_/g, '-')}-input`;

        handleError(form, convertedKey, `Please enter a valid ${fieldInfo[0]}`, fieldInfo[1]);

        errorsHandled += 1;
      }
    });
  }

  scrollToElement(form, ".js-error-message");

  if (errorsHandled === 0) { toggleErrorMessages(enquiry, true); }

  toggleSubmitButtons(enquiry, true);
};

const postForm = (enquiry, form, data, callbacks) => () => {
  const firstName = form.querySelector("input[name='enquiry[first_name]']").value;
  const firstNameTitle = firstName.charAt(0).toUpperCase() + firstName.slice(1);
  const slug = window.location.pathname.split("/")[1];
  const enquiryPrice = enquiry.querySelector(".price");
  const price = enquiryPrice ? enquiryPrice.textContent.slice(1) : null;

  $.post(form.action, data)
    .done(res => {
      enquiry.querySelector(".js-first-name").textContent = firstNameTitle;
      enquiry.classList.add("has-succeeded");
      const scrollToEl = form.querySelector(".has-succeeded .enquiry__message--success");
      if (scrollToEl) {
        const navOffset = 150;
        // eslint-disable-next-line no-mixed-operators,max-len
        const topOfElement = window.pageYOffset + scrollToEl.getBoundingClientRect().top - navOffset;
        window.scroll({ top: topOfElement, behavior: "smooth" });
      }

      trackEnquiry(res.gtm_data, res.lead_value);

      if (res.is_booking && res.payment_data.amount) {
        submitBooking(enquiry, res.payment_data);
      } else {
        emarsys(res.emarsys_data);
      }

      if (price && slug === "the-masters") {
        trackRemarketing(slug, price);
      }

      if (callbacks.done) {
        callbacks.done();
      }
    })
    .fail(res => {
      getErrorMessages(enquiry, res, form);
      trackServerSideEnquiryError(form);
    });
};

export const handleSubmit = (enquiry, callbacks = {}) => e => {
  const form = e.target;
  const formData = $(form).serializeArray();
  e.preventDefault();

  const arrivalDate = formData.find(item => item.name.includes("enquiry[arrival_date]"));
  const groupSize = formData.find(item => item.name.includes("enquiry[group_size]"));
  const phone = formData.find(item => item.name.includes("enquiry[phone]"));
  const isPhoneValid = PHONE_PATTERN.test(phone ? phone.value : "");
  const email = form.querySelector(".js-email-input");
  const isEmailValid = EMAIL_PATTERN.test(email ? email.value : "");
  const firstName = form.querySelector(".js-first-name-input").value;
  const isFirstNameValid = firstName && typeof firstName === 'string';
  const lastName = form.querySelector(".js-last-name-input").value;
  const isLastNameValid = lastName && typeof lastName === 'string';

  if (isPhoneValid && isEmailValid && isLastNameValid && isFirstNameValid && (!groupSize || groupSize.value > "0") && (!arrivalDate || arrivalDate.value.length > 0)) {
    toggleSubmitButtons(enquiry, false);
    toggleErrorMessages(enquiry, false);

    $.getJSON("/csrf-token")
      .done(res => formData.push({ name: res.param, value: res.token }))
      .always(postForm(enquiry, form, formData, callbacks));
  }
};

export const missingOrInvalidField = target => {
  const errorPrefix = target.validity.valueMissing ? "Missing" : "Invalid";
  return `${errorPrefix} Field: ${target.name}`;
};

export const setSubtitle = (enquiry, { nights, rounds, date, override }) => {
  const subtitle = enquiry.querySelector(".js-enquiry-subtitle");

  if (!subtitle) {
    return;
  }

  if (override) {
    subtitle.textContent = override;

    return;
  }

  const dateObj = dayjs(date, UK_DATE_FORMAT);
  const onDate = dateObj.isValid()
    ? ` on <span class="nowrap">
        ${dateObj.format(SUMMARY_DATE_FORMAT_SHORT)}
      </span>`
    : "";
  const nightsAndRounds = { night: nights, round: rounds };
  const subtitleText = Object.keys(nightsAndRounds)
    .filter(key => !!nightsAndRounds[key])
    .map(key => pluralize(key, nightsAndRounds[key], true))
    .join(", ")
    .concat(onDate);

  subtitle.innerHTML = subtitleText;
};

export const setTitle = (enquiry, text) => {
  const title = enquiry.querySelector(".js-enquiry-title");
  const dest = enquiry.querySelector(".js-destination");

  if (title && text) {
    title.textContent = text;
  } else if (title && dest) {
    title.textContent = dest.options[dest.selectedIndex].text;
  }
};

export const setCountryDropdownVisibility = (enquiry, setting) => {
  const dropdownElement = enquiry.querySelector(".js-country-dropdown");
  const countrySelect = dropdownElement.querySelector("select");

  if (setting) {
    dropdownElement.style.display = "block";
    countrySelect.removeAttribute("disabled");
  } else {
    dropdownElement.style.display = "none";
    countrySelect.setAttribute("disabled", "disabled");
  }
};

export const setButtonText = (enquiry, text) => {
  const button = enquiry.querySelector(".js-cta-button");

  if (button && text) {
    button.textContent = text;
  }
};

export const setAdditionalInfoPlaceholder = (enquiry, placeholder) => {
  const textBox = enquiry.querySelector(".js-additional-info");

  if (textBox && placeholder) {
    textBox.placeholder = placeholder;
  }
};

export const setCallUsText = (enquiry, text) => {
  const element = enquiry.querySelector(".js-call-us");

  if (element && text) {
    element.textContent = text;
  }
};

const addErrorMsg = (form, msg) => {
  const offerSubtitle = form.querySelector(".venue__offer__subtitle");
  const errorMsgTag = form.querySelector(".venue__offer__subtitle-container .js-error-message");
  if (errorMsgTag) {
    errorMsgTag.remove();
  }

  const htmlErrors = document.createElement("div");
  htmlErrors.classList.add("error-message", "js-error-message");
  htmlErrors.innerText = msg;
  offerSubtitle.after(htmlErrors);
};

const handleToggle = (enquiry, toggleSelector) => e => {
  if (e.target.closest(toggleSelector)) {
    const date = enquiry.querySelector(".js-arrival-date").value;
    const [nights, rounds] = ["nights", "rounds"].map(key =>
      [].slice
        .call(enquiry.querySelectorAll(`.js-enquiry-${key}`))
        .map(node => node.value)
        .reduce((sum, curr) => sum + parseInt(curr, 10), 0)
    );

    e.preventDefault();
    enquiry.classList.toggle("is-shifted");
    enquiry.classList.remove("has-failed");
    setTitle(enquiry);
    setSubtitle(enquiry, { nights, rounds, date });
  }
};

const handleFlightsToggle = (enquiry, toggleSelector) => e => {
  if (e.target.closest(toggleSelector)) {
    enquiry.querySelector(".js-airport-input").classList.toggle("hide", !e.target.checked);
  }
};

const hidePhoneFields = () => {
  const phoneFields = document.querySelector(".js-phone-consent");
  if (phoneFields) {
    phoneFields.classList.add("hide");
  }
};

export const resetModal = enquiry => {
  const modal = enquiry.closest(".modal");

  if (modal) {
    $(modal).on("hidden.bs.modal", function () {
      const enquireButton = enquiry.querySelector(".js-enquiry-submit");
      const bookButton = enquiry.querySelector(".js-booking-submit");
      const defaultCheckedBoxes = enquiry.querySelectorAll(".js-checked-box");
      const defaultUncheckedBoxes = enquiry.querySelectorAll(".js-unchecked-box");

      enquiry.classList.remove("has-failed", "has-succeeded", "is-shifted");
      enquireButton.removeAttribute("disabled");

      defaultCheckedBoxes.forEach(function (checkBox) {
        checkBox.checked = true;
      });
      defaultUncheckedBoxes.forEach(function (checkBox) {
        checkBox.checked = false;
      });
      if (bookButton) bookButton.classList.remove("hide");

      hidePhoneFields();
    });
  }
};

const invalidEnquiryData = form => {
  const inputs = [].slice.call(form.getElementsByTagName("input"));
  const formData = inputs.reduce((obj, input) => ({ [input.name]: input.value, ...obj }), {});

  const validationErrors = inputs.filter(input => !input.validity.valid).map(missingOrInvalidField);

  const breadcrumbs = document.querySelector(".js-breadcrumbs");

  const supplementedFormData = {
    ...formData,
    "enquiry[url]": window.location.href,
    "enquiry[validation_errors]": validationErrors,
    "enquiry[page]": document.title,
    "enquiry[breadcrumbs]": breadcrumbs ? breadcrumbs.innerText : ""
  };

  return supplementedFormData;
};

const validateDate = (form) => {
  const date = form.querySelector(".js-offer-date");

  return !date || date.value.length > 0; // return true if no date field or field has value
};

const dateFieldValidation = (form) => {
  const isDateValid = validateDate(form);

  if (!isDateValid) {
    const container = form.querySelector(".js-offer-date-container");
    if (container) {
      container.classList.add("is-invalid");
    }

    const msgError = 'Please select a date';
    addErrorMsg(form, msgError);
  }
  return isDateValid;
};

const validatePax = (form) => {
  const pax = form.querySelector(".js-offer-pax");
  const selectedPax = pax ? pax.options[pax.selectedIndex] : 0;
  return !pax || selectedPax.value > 0;
};

const paxFieldValidation = (form) => {
  const isPaxValid = validatePax(form);
  if (!isPaxValid) {
    const container = form.querySelector(".js-offer-pax-container");
    if (container) {
      container.classList.add("is-invalid");
    }

    const msgError = 'Select no. of people';
    addErrorMsg(form, msgError);
  }
  return isPaxValid;
};

const removeHtmlTagErrors = (form, className, element = "div") => {
  const selector = form.querySelector(className);

  if (selector) {
    // Remove Html tag errors
    selector.closest(element).querySelectorAll(".js-error-message").forEach(e => {
      e.remove();
    });
  }
};

export const addInvalidSubmitHandler = form => {
  form.querySelector("[type=submit]").addEventListener("click", () => {
    // Remove tag html errors
    removeHtmlTagErrors(form, ".js-email-input", ".field-group");
    removeHtmlTagErrors(form, ".js-phone-input", ".field-group");
    removeHtmlTagErrors(form, ".js-first-name-input", ".field-group");
    removeHtmlTagErrors(form, ".js-last-name-input", ".field-group");
    removeHtmlTagErrors(form, ".venue__offer__subtitle", ".venue__offer__subtitle-container");

    // Validate first name
    let firstName = form.querySelector(".js-first-name-input");
    firstName = firstName ? firstName.value : "";
    if (firstName === "") {
      handleError(form, ".js-first-name-input", "Please enter a valid first name");
    }

    // Validate last name
    let lastName = form.querySelector(".js-last-name-input");
    lastName = lastName ? lastName.value : "";
    if (lastName === "") {
      handleError(form, ".js-last-name-input", "Please enter a valid last name");
    }

    // Validate email
    const email = form.querySelector(".js-email-input");
    const isEmailValid = EMAIL_PATTERN.test(email ? email.value : "");
    if (!isEmailValid) {
      handleError(form, ".js-email-input", "Please enter a valid email address");
    }

    // Validate phone
    const phone = form.querySelector(".js-phone-input");
    const isPhoneValid = PHONE_PATTERN.test(phone ? phone.value : "");
    if (!isPhoneValid) {
      handleError(form, ".js-phone-input", "Please enter a valid phone number", ".layout__item__field");
    }

    // Validate arrival date
    const isDateValid = dateFieldValidation(form);
    const isPaxValid = paxFieldValidation(form);

    if (!isDateValid && !isPaxValid) {
      const errorMsg = form.querySelector(".js-error-message");
      if (errorMsg) {
        errorMsg.remove();
      }

      const msgError = 'Select no. of people & a date';
      addErrorMsg(form, msgError);
    }
    scrollToElement(form, ".js-error-message");

    scrollToElement(form, ".js-error-message");

    if (isPaxValid && isDateValid && isEmailValid && !form.checkValidity()) {
      const invalidFields = [].slice.call(form.querySelectorAll(":invalid"));
      const phoneFieldValid = !invalidFields.some(input => input.name === "enquiry[phone]");
      const emailFieldValid = !invalidFields.some(input => input.name === "enquiry[email]");

      if (phoneFieldValid || emailFieldValid) {
        const data = queryString(invalidEnquiryData(form));

        fetch(`/incomplete_enquiries?${data}`, {
          method: "POST"
        });
      }
    }
  });
};

const addDateFocusHandler = (enquiry) => {
  const dateInput = enquiry.querySelector(".js-offer-date");
  if (dateInput) {
    dateInput.addEventListener("focus", () => {
      const dateContainer = enquiry.querySelector(".js-offer-date-container.is-invalid");
      if (dateContainer) {
        dateContainer.classList.remove("is-invalid");
      }
      const errorMsg = enquiry.querySelector(".js-error-message");
      if (errorMsg) {
        errorMsg.remove();
      }
    });
  }
};

const addPaxFocusHandler = (enquiry) => {
  const paxInput = enquiry.querySelector(".js-offer-pax");
  if (paxInput) {
    paxInput.addEventListener("focus", () => {
      const paxContainer = enquiry.querySelector(".js-offer-pax-container.is-invalid");
      if (paxContainer) {
        paxContainer.classList.remove("is-invalid");
      }
      const errorMsg = enquiry.querySelector(".js-error-message");
      if (errorMsg) {
        errorMsg.remove();
      }
    });
  }
};

const fieldFocusHandler = (enquiry, className, element = "div") => {
  const fieldFocus = enquiry.querySelector(className);
  if (fieldFocus) {
    fieldFocus.addEventListener("focus", () => {
      let selectorParentTag = fieldFocus;
      selectorParentTag = selectorParentTag.closest(element);

      const warningTag = selectorParentTag.querySelector(".fa-exclamation-triangle");
      if (warningTag) {
        warningTag.classList.add("hide");
      }

      const errorMsgTag = selectorParentTag.querySelector(".js-error-message");
      if (errorMsgTag) {
        errorMsgTag.remove();
      }
    });
  }
};

export const formFieldFocusHandler = enquiry => {
  fieldFocusHandler(enquiry, ".js-phone-input", ".field-group");

  fieldFocusHandler(enquiry, ".js-email-input", ".field-group");

  fieldFocusHandler(enquiry, ".js-first-name-input", ".field-group");

  fieldFocusHandler(enquiry, ".js-last-name-input", ".field-group");
};

export const initLoadCountryPhone = (enquiry) => {
  const containers = enquiry.querySelectorAll(".js-phone-container");
  if (containers) {
    initDropdownEvents(containers);
  }
};

export const initEnquiry = enquiry => {
  const form = enquiry.querySelector(".js-enquiry-form");

  resetModal(enquiry);

  initForm(form, { submit: handleSubmit(enquiry) });

  enquiry.addEventListener("click", handleToggle(enquiry, ".js-enquiry-toggle"));

  enquiry.addEventListener("change", handleFlightsToggle(enquiry, ".js-flights-toggle"));

  addDateFocusHandler(enquiry);

  addPaxFocusHandler(enquiry);

  formFieldFocusHandler(enquiry);

  addInvalidSubmitHandler(form);

  initLoadCountryPhone(enquiry);
};



// WEBPACK FOOTER //
// ./utils/Enquiries.js